import { MessageCircle, Plus, Send, Trash2 } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { Announcement, api, Board, BoardComment, User } from "../api";
import { BlockRenderer, paragraph } from "../components/block-editor";
import { ConfirmDialog, Empty, Loading, ModalFrame, PageHead, Pagination, RowActionMenu } from "../components/ui";
import { koDate, koStamp } from "../lib/format";
import { NoticeForm } from "./Settings";

export function BoardsPage({ viewerId, users, onChanged }: { viewerId: string; users: User[]; onChanged: (message: string) => Promise<void> }) {
  const [boards, setBoards] = useState<Board[] | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [posts, setPosts] = useState<Announcement[] | null>(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [open, setOpen] = useState<Announcement | null>(null);
  const [removing, setRemoving] = useState<Announcement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const selected = boards?.find((board) => board.id === selectedId);
  const authors = new Map(users.map(user => [user.id, user.displayName]));
  const canEdit = (post: Announcement) => !!selected?.canWrite && (selected.canManage || post.createdBy === viewerId);

  const loadBoards = useCallback(async () => {
    const next = await api.boards();
    setBoards(next);
    setSelectedId((current) => next.some((item) => item.id === current) ? current : next[0]?.id || "");
  }, []);
  useEffect(() => { void loadBoards().catch((reason) => setError(reason instanceof Error ? reason.message : "게시판을 불러오지 못했습니다.")); }, [loadBoards]);
  useEffect(() => {
    if (!selectedId) { setPosts([]); return; }
    setPosts(null);
    let active = true;
    void api.boardPosts(selectedId, page, 12).then((next) => { if (active) { setPosts(next.items); setPages(next.totalPages); } })
      .catch((reason) => { if (active) { setError(reason instanceof Error ? reason.message : "게시글을 불러오지 못했습니다."); setPosts([]); } });
    return () => { active = false; };
  }, [page, selectedId]);

  return <>
    <PageHead title="게시판" meta="조직의 공지와 소식을 권한 범위에 맞게 공유합니다" controls={selected?.canWrite
      ? <button type="button" className="primary" onClick={() => setWriting(true)}><Plus size={15} /> 글쓰기</button> : undefined} />
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>닫기</button></div>}
    <nav className="section-nav board-tabs">{boards?.map((board) => <button type="button" key={board.id}
      className={board.id === selectedId ? "on" : ""} onClick={() => { setSelectedId(board.id); setPage(0); }}>{board.name}</button>)}</nav>
    <section className="section-card board-page-card">
      {selected && <header><div><span className="mono-label">BOARD</span><h2>{selected.name}</h2><p>{selected.description}</p></div></header>}
      {!boards || posts === null ? <Loading layout="list" label="게시글을 불러오는 중" />
        : boards.length === 0 ? <Empty title="볼 수 있는 게시판이 없습니다" copy="게시판 관리자가 읽기 범위를 설정하면 이곳에 표시됩니다." />
          : posts.length === 0 ? <Empty title="등록된 게시글이 없습니다" copy={selected?.canWrite ? "첫 게시글을 작성해 보세요." : "새 게시글이 등록되면 이곳에 표시됩니다."} />
            : <div className="board-post-list">{posts.map((post) => <article key={post.id}>
              <button type="button" className="board-post-open" onClick={() => setOpen(post)}><span>{post.status === "DRAFT" && <em className="pill muted">임시저장</em>}{post.pinned && <em className="pill">고정</em>}<b>{post.untrustedTitle || '제목 없는 게시글'}</b></span>
                <span className="board-post-meta"><span>{authors.get(post.createdBy) || '알 수 없는 작성자'}</span><time>{koDate(post.publishedAt || post.updatedAt)}</time></span></button>
              <RowActionMenu label={`${post.untrustedTitle || '게시글'} 더보기`} disabled={!canEdit(post)} actions={canEdit(post) ? [
                { label: '수정', onClick: () => { setError(''); setEditing(post); } },
                { label: '삭제', danger: true, onClick: () => { setError(''); setRemoving(post); } },
              ] : []} />
            </article>)}</div>}
      <Pagination page={page} totalPages={pages} onPage={setPage} />
    </section>
    {writing && selected && <ModalFrame title={`${selected.name} 글쓰기`} eyebrow="NEW POST" wide onClose={() => setWriting(false)}>
      {error && <p className="error-box" role="alert">{error}</p>}
      <NoticeForm item={null} busy={busy} onCancel={() => setWriting(false)} onError={setError} onSubmit={async (body) => {
        setBusy(true);
        try { await api.createBoardPost(selected.id, body); setWriting(false); setPage(0); await onChanged(body.status === "DRAFT" ? "게시글을 임시저장했습니다." : "게시글을 발행했습니다.");
          const next = await api.boardPosts(selected.id, 0, 12); setPosts(next.items); setPages(next.totalPages);
        } catch (reason) { setError(reason instanceof Error ? reason.message : '게시글을 저장하지 못했습니다.'); } finally { setBusy(false); }
      }} />
    </ModalFrame>}
    {editing && selected && <ModalFrame title="게시글 수정" eyebrow={selected.name} wide onClose={() => setEditing(null)}>
      {error && <p className="error-box" role="alert">{error}</p>}
      <NoticeForm item={editing} busy={busy} onCancel={() => setEditing(null)} onError={setError} onSubmit={async (body) => {
        setBusy(true);
        try { await api.updateBoardPost(selected.id, editing.id, body); setEditing(null); const next = await api.boardPosts(selected.id, page, 12); setPosts(next.items); setPages(next.totalPages); await onChanged("게시글을 수정했습니다."); }
        catch (reason) { setError(reason instanceof Error ? reason.message : '게시글을 수정하지 못했습니다.'); } finally { setBusy(false); }
      }} />
    </ModalFrame>}
    {removing && <ConfirmDialog title="게시글을 삭제할까요?" eyebrow="DELETE POST" confirmLabel="삭제" busy={busy} onClose={() => setRemoving(null)} onConfirm={async () => {
      setBusy(true); setError('');
      try {
        await api.deleteBoardPost(removing.boardId, removing.id); setRemoving(null);
        const nextPage = posts?.length === 1 && page > 0 ? page - 1 : page;
        if (nextPage !== page) setPage(nextPage);
        else { const next = await api.boardPosts(removing.boardId, page, 12); setPosts(next.items); setPages(next.totalPages); }
        await onChanged('게시글을 삭제했습니다.');
      } catch (reason) { setError(reason instanceof Error ? reason.message : '게시글을 삭제하지 못했습니다.'); } finally { setBusy(false); }
    }}><p>{removing.untrustedTitle}을(를) 삭제합니다. 삭제한 글은 되돌릴 수 없습니다.</p>{error && <p role="alert">{error}</p>}</ConfirmDialog>}
    {open && selected && <PostModal board={selected} post={open} author={authors.get(open.createdBy) || '알 수 없는 작성자'} onClose={() => setOpen(null)} />}
  </>;
}

function PostModal({ board, post, author, onClose }: { board: Board; post: Announcement; author: string; onClose: () => void }) {
  const [comments, setComments] = useState<BoardComment[] | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => board.commentsEnabled
    ? api.boardComments(board.id, post.id).then(setComments)
    : Promise.resolve(), [board.commentsEnabled, board.id, post.id]);
  useEffect(() => { void load().catch(reason => { setError(reason instanceof Error ? reason.message : '댓글을 불러오지 못했습니다.'); setComments([]); }); }, [load]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try { await api.createBoardComment(board.id, post.id, body); setBody(""); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : '댓글을 등록하지 못했습니다.'); } finally { setBusy(false); }
  }
  return <ModalFrame title={post.untrustedTitle} eyebrow={board.name} wide onClose={onClose}>
    <div className="board-post-view"><p className="board-post-meta"><span>{author}</span><time>{koStamp(post.publishedAt || post.createdAt)}</time></p>
      <BlockRenderer blocks={post.blocks?.length ? post.blocks : [paragraph(post.untrustedBody)]} loadImage={api.downloadFile} />
    </div>
    {board.commentsEnabled && <section className="board-comments">
      {error && <p role="alert" className="error-box">{error}</p>}
      <h3><MessageCircle size={16} /> 댓글 {comments?.length || 0}</h3>
      {comments === null ? <Loading layout="list" label="댓글을 불러오는 중" /> : comments.map((comment) => <article key={comment.id}>
        <div><b>{comment.authorName}</b><time>{koStamp(comment.createdAt)}</time></div><p>{comment.body}</p>
        {comment.canDelete && <button type="button" aria-label="댓글 삭제" onClick={async () => { try { await api.deleteBoardComment(board.id, post.id, comment.id); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : '댓글을 삭제하지 못했습니다.'); } }}><Trash2 size={13} /></button>}
      </article>)}
      <form onSubmit={submit}><textarea aria-label="댓글" value={body} maxLength={5000} placeholder="댓글을 입력하세요" onChange={(event) => setBody(event.target.value)} />
        <button type="submit" className="primary" disabled={busy || !body.trim()}><Send size={14} /> 등록</button></form>
    </section>}
  </ModalFrame>;
}
